import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from "antd";
import { MailOutlined, CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { Offer, getAllOffers, UserPurchase, checkUserFreeOffer, FreeOfferStatus } from "../../services/offreService";
import { getUserQuota } from "../../services/Quota_Service";
import OfferCards from "../OfferCards";

interface QuotaState {
  dailyUsed: number;
  monthlyUsed: number;
  totalUsed: number;
  dailyQuota: number;
  monthlyQuota: number;
}

interface UserQuotaTabProps {
  user: any;
  authUserEmail?: string;
}

const UserQuotaTab: React.FC<UserQuotaTabProps> = ({ user, authUserEmail }) => {
  const [quotas, setQuotas] = useState<QuotaState>({
    dailyUsed: 0,
    monthlyUsed: 0,
    totalUsed: 0,
    dailyQuota: 0,
    monthlyQuota: 0
  });
  
  const [activeOffers, setActiveOffers] = useState<Offer[]>([]);
  const [showOffers, setShowOffers] = useState(false);
  const [freeOfferStatus, setFreeOfferStatus] = useState<FreeOfferStatus>({
    hasFreeOffer: false,
  });

  // Calculs des pourcentages
  const dailyPercent = quotas.dailyQuota ? Math.min((quotas.dailyUsed / quotas.dailyQuota) * 100, 100) : 0;
  const monthlyPercent = quotas.monthlyQuota ? Math.min((quotas.monthlyUsed / quotas.monthlyQuota) * 100, 100) : 0;

  const handleAllQuota = useCallback(async () => {
    if (!showOffers) {
      try {
        const offers = await getAllOffers();
        const active = offers.filter((offer: Offer) => offer.status === "active");
        setActiveOffers(active);
      } catch (error) {
        toast.error("Impossible de récupérer les offres disponibles");
        return;
      }
    }
    setShowOffers(!showOffers);
  }, [showOffers]);

  useEffect(() => {
    const fetchFreeOfferStatus = async () => {
      const email = localStorage.getItem("userEmail");
      if (!email) return;
      const status = await checkUserFreeOffer(email);
      setFreeOfferStatus(status);
    };
    fetchFreeOfferStatus();
  }, []);

  useEffect(() => {
    const fetchQuotas = async () => {
      if (!authUserEmail) return;
      
      try {
        const quota = await getUserQuota(authUserEmail);
        setQuotas({
          dailyUsed: quota.dailyUsed,
          monthlyUsed: quota.monthlyUsed,
          totalUsed: quota.totalUsed,
          dailyQuota: quota.dailyQuota,
          monthlyQuota: quota.monthlyQuota
        });
        await handleAllQuota();
      } catch (error) {
        console.error("Erreur lors de la récupération des quotas:", error);
      }
    };

    fetchQuotas();
  }, [authUserEmail, handleAllQuota]);

  return (
    <>
      {freeOfferStatus.hasFreeOffer && (
        <div className="mt-3">
          <div
            className="d-flex align-items-start p-4 rounded-3"
            style={{
              backgroundColor: "rgba(75, 176, 192, 0.1)",
              border: "1px solid rgba(75, 176, 192, 0.25)",
            }}
          >
            <div className="flex-shrink-0 bg-opacity-25 p-3 rounded-circle" style={{ backgroundColor: "rgba(75, 176, 192, 0.2)" }}>
              <i className="bi bi-gift fs-4"></i>
            </div>
            <div className="ms-4">
              <h5 className="fw-bold mb-2" style={{ color: "#4bb0c0" }}>
                <i className="bi bi-check-circle-fill me-2" style={{ color: "#4bb0c0" }}></i>
                Votre mois gratuit est activé
              </h5>
              
              <div className="d-flex align-items-center mb-3 flex-wrap gap-3">
                <div className="me-4">
                  <div className="text-muted small">Durée</div>
                  <div className="fw-semibold">1 mois gratuit</div>
                </div>
              </div>
              
              <div className="alert alert-light border mb-4">
                <p className="mb-2">
                  <strong>📢 Offre limitée à 1 mois par utilisateur</strong>
                </p>
                <p className="mb-0 small text-muted">
                  Vous bénéficiez actuellement de votre période d'essai gratuit de 30 jours. 
                  Cette offre promotionnelle n'est utilisable qu'une seule fois.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <h3 className="h4 fw-bold mb-2 text-gradient" style={{
        background: 'linear-gradient(135deg, #4BB0C0 0%, #2E8C99 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        Mes abonnements actifs
      </h3>

      {(user?.purchases?.length > 0 || user?.hasFreeOffer) && (
        <div className="mb-5">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <p className="text-muted mb-0">
                {`Vous bénéficiez de ${user.purchases.length + (user.hasFreeOffer ? 1 : 0)} offre${user.purchases.length + (user.hasFreeOffer ? 1 : 0) > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
            {user.purchases.map((purchase: UserPurchase, idx: number) => {
              const isExpired = purchase.purchasedAt
                ? new Date(purchase.purchasedAt) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                : false;

              return (
                <div className="col" key={idx}>
                  <div className={`card h-100 shadow-sm ${isExpired ? 'opacity-75' : ''}`}>
                    <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">{purchase.offer?.name || 'Offre sans nom'}</span> 
                      <span>{purchase.billing_period === "yearly" ? "Abonnement Annuel" : "Abonnement Mensuel"}</span>
                      {isExpired && <span className="badge bg-warning">Expiré</span>}
                    </div>
                    <div className="card-body">
                      <div className="d-flex gap-2 mb-3">
                        <div className="flex-fill text-center p-2 border rounded bg-opacity-10 text-primary"
                          style={{ background: '#4bb0c0', color: 'white' }}>
                          <div className="fw-bold fs-4" style={{ color: 'white' }}>{purchase.offer?.daily_emails || 0}</div>
                          <small style={{ color: 'white' }}>Emails/jour</small>
                        </div>
                        <div className="flex-fill text-center p-2 border rounded bg-opacity-10 text-warning" 
                          style={{ background: '#f4a261', color: 'white' }}>
                          <div className="fw-bold fs-4" style={{ color: 'white' }}>{purchase.offer?.monthly_emails || 0}</div>
                          <small style={{ color: 'white' }}>Emails/mois</small>
                        </div>
                        <div className="flex-fill text-center p-2 border rounded bg-opacity-10 text-warning" 
                          style={{ background: '#4bb0c0', color: 'white' }}>
                          <div className="fw-bold fs-4" style={{ color: 'white' }}>{purchase.offer?.yearly_emails || 0}</div>
                          <small style={{ color: 'white' }}>Emails/an</small>
                        </div>
                      </div>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between px-0 py-1">
                          <span>Prix</span>
                          <span className="fw-bold">{purchase.billing_period === "monthly" ? `${purchase.offer?.monthly_price || 0} €/mois` : `${purchase.offer?.yearly_price || 0} €/an`}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between px-0 py-1">
                          <span>Actif depuis</span>
                          <span>
                            {purchase.purchasedAt
                              ? new Date(purchase.purchasedAt).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })
                              : 'N/A'}
                          </span>
                        </li>
                      </ul>
                      {purchase.invoiceUrl && (
                        <div 
                          className="mt-3"
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            backgroundColor: '#4BB0C0',
                            padding: '10px',
                            borderRadius: '8px',
                          }}
                        >
                          <a
                            href={purchase.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-light fw-bold w-100 d-flex align-items-center justify-content-center"
                            style={{
                              color: '#4bb0c0',
                              borderRadius: '6px',
                              fontWeight: 600,
                              gap: '5px',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                              transition: 'transform 0.1s ease-in-out',
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                            onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              fill="currentColor" 
                              className="bi bi-file-earmark-text" 
                              viewBox="0 0 16 16"
                            >
                              <path d="M5.5 7.5A.5.5 0 0 1 6 7h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5zm0 2A.5.5 0 0 1 6 9h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5z"/>
                              <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h6.5L14 4.5zm-3-.5V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4h-2a1 1 0 0 1-1-1z"/>
                            </svg>
                            Voir la facture
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        {(!user || !user.purchases || user.purchases.length === 0) && (
          <div className="alert alert-info">
            <strong>Aucune offre achetée</strong> - Les offres que vous achetez apparaîtront ici avec les quotas associés.
          </div>
        )}
      </div>

      {user && (
        <div style={{ marginBottom: 24 }}>
          <h3 className="h4 fw-bold mb-2 text-gradient" style={{
            background: 'linear-gradient(135deg, #4BB0C0 0%, #2E8C99 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Suivi de quotas
          </h3>
          
          <Row gutter={[16, 16]}>
            {[
              {
                icon: <MailOutlined />,
                title: "Aujourd'hui",
                used: quotas.dailyUsed,
                quota: quotas.dailyQuota,
                percent: dailyPercent,
                remaining: Math.max(quotas.dailyQuota - quotas.dailyUsed, 0),
                color: dailyPercent >= 80 ? '#EF214F' : '#4BB0C0',
              },
              {
                icon: <CalendarOutlined />,
                title: "Ce mois-ci",
                used: quotas.monthlyUsed,
                quota: quotas.monthlyQuota,
                percent: monthlyPercent,
                remaining: Math.max(quotas.monthlyQuota - quotas.monthlyUsed, 0),
                color: monthlyPercent >= 80 ? '#EF214F' : '#4BB0C0',
              },
            ].map((item, idx) => (
              <Col xs={24} sm={12} md={12} lg={12} key={idx}>
                <div className="quota-card" style={{
                  background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                  borderRadius: 16,
                  padding: 20,
                  border: '1px solid rgba(75, 176, 192, 0.2)',
                  boxShadow: `
                    0 4px 6px -1px rgba(0, 0, 0, 0.05),
                    0 2px 4px -1px rgba(0, 0, 0, 0.03),
                    inset 0 -2px 0 rgba(75, 176, 192, 0.1)
                  `,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    background: `radial-gradient(circle, ${item.color}15 0%, transparent 70%)`,
                    borderRadius: '50%',
                    opacity: 0.6
                  }} />

                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: `linear-gradient(135deg, ${item.color}, ${item.color === '#4BB0C0' ? '#2F94A1' : '#FF6B8B'})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${item.color}40`
                      }}>
                        {React.cloneElement(item.icon, {
                          style: { fontSize: 20, color: 'white', fontWeight: 'bold' }
                        })}
                      </div>
                      <div>
                        <span style={{
                          fontSize: 15,
                          color: '#1a1a1a',
                          display: 'block',
                          letterSpacing: '-0.01em',
                          fontWeight: 600
                        }}>
                          {item.title}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: '#666',
                          display: 'block',
                          marginTop: 2
                        }}>
                          Emails envoyés
                        </span>
                      </div>
                    </div>

                    <div style={{
                      background: item.percent >= 80
                        ? 'linear-gradient(135deg, #EF214F, #FF3B6B)'
                        : 'linear-gradient(135deg, #4BB0C0, #2F94A1)',
                      padding: '6px 14px',
                      borderRadius: 20,
                      boxShadow: `0 2px 8px ${item.color}30`
                    }}>
                      <span style={{
                        fontSize: 14,
                        color: 'white',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        fontWeight: 600
                      }}>
                        {item.percent.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20, position: 'relative' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      marginBottom: 8
                    }}>
                      <div>
                        <span style={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: '#1a1a1a',
                          lineHeight: 1,
                          background: `linear-gradient(90deg, ${item.color}, ${item.color === '#4BB0C0' ? '#2F94A1' : '#EF214F'})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}>
                          {item.used}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          <div style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: '#666'
                          }} />
                          <span style={{ fontSize: 13, color: '#666' }}>
                            sur {item.quota} emails autorisés
                          </span>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 15, color: '#888', display: 'block', marginBottom: 2, fontWeight: 500 }}>
                          Restant
                        </span>
                        <span style={{
                          fontSize: 18,
                          color: '#F4A261',
                          display: 'block',
                          fontWeight: 600
                        }}>
                          {item.remaining}
                        </span>
                      </div>
                    </div>

                    <div style={{
                      height: 10,
                      background: 'linear-gradient(to right, #f0f0f0, #f8f8f8)',
                      borderRadius: 5,
                      overflow: 'hidden',
                      position: 'relative',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${item.percent}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        style={{
                          height: '100%',
                          background: `linear-gradient(90deg, 
                            ${item.color}99, 
                            ${item.color === '#4BB0C0' ? '#2F94A1' : '#EF214F'}DD
                          )`,
                          borderRadius: 5,
                          position: 'relative',
                          boxShadow: `0 0 12px ${item.color}40`
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '30%',
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0.3), transparent)',
                          borderRadius: '5px 5px 0 0'
                        }} />

                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translate(50%, -50%)',
                          width: 16,
                          height: 16,
                          background: 'white',
                          border: `2px solid ${item.color}`,
                          borderRadius: '50%',
                          boxShadow: `0 0 8px ${item.color}`
                        }} />
                      </motion.div>

                      <div style={{
                        position: 'absolute',
                        bottom: -20,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 15,
                        color: '#888'
                      }}>
                        <span>0</span>
                        <span>50%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {[
              {
                icon: <ClockCircleOutlined />,
                title: 'Total historique',
                value: quotas.totalUsed.toLocaleString(),
                color: '#4BB0C0',
                description: 'Depuis le début',
                trend: 'up',
                gradient: 'linear-gradient(135deg, #4BB0C0, #2F94A1)'
              },
              {
                icon: <MailOutlined />,
                title: 'Disponible aujourd\'hui',
                value: Math.max(quotas.dailyQuota - quotas.dailyUsed, 0),
                color: '#2F94A1',
                description: quotas.dailyUsed >= quotas.dailyQuota ? 'Quota épuisé' : 'Encore disponible',
                trend: quotas.dailyUsed >= quotas.dailyQuota ? 'down' : 'up',
                gradient: 'linear-gradient(135deg, #2F94A1, #4BB0C0)'
              },
              {
                icon: <CalendarOutlined />,
                title: 'Disponible ce mois',
                value: Math.max(quotas.monthlyQuota - quotas.monthlyUsed, 0),
                color: '#F4A261',
                description: quotas.monthlyUsed >= quotas.monthlyQuota ? 'Quota atteint' : 'En cours',
                trend: quotas.monthlyUsed >= quotas.monthlyQuota ? 'down' : 'up',
                gradient: 'linear-gradient(135deg, #F4A261, #FF9E6D)'
              },
            ].map((item, idx) => (
              <Col xs={24} sm={12} md={8} lg={8} key={idx}>
                <motion.div
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="stat-card"
                  style={{
                    background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                    borderRadius: 16,
                    padding: 24,
                    border: '1px solid rgba(75, 176, 192, 0.15)',
                    boxShadow: `
                      0 4px 6px -1px rgba(0, 0, 0, 0.05),
                      0 2px 4px -1px rgba(0, 0, 0, 0.03)
                    `,
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 80,
                    height: 80,
                    opacity: 0.05,
                    background: `
                      radial-gradient(circle at 30% 30%, ${item.color} 0%, transparent 50%),
                      radial-gradient(circle at 70% 70%, ${item.color} 0%, transparent 50%)
                    `
                  }} />

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: item.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 6px 16px ${item.color}40`
                    }}>
                      {React.cloneElement(item.icon, {
                        style: { fontSize: 24, color: 'white' }
                      })}
                    </div>

                    <div style={{
                      padding: '4px 10px',
                      borderRadius: 12,
                      background: item.trend === 'up' ? 'rgba(75, 176, 192, 0.1)' : 'rgba(239, 33, 79, 0.1)',
                      border: `1px solid ${item.trend === 'up' ? 'rgba(75, 176, 192, 0.3)' : 'rgba(239, 33, 79, 0.3)'}`
                    }}>
                      <span style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: item.trend === 'up' ? '#4BB0C0' : '#EF214F',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        {item.trend === 'up' ? '✓ Disponible' : '⚠ Limite'}
                      </span>
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <span style={{
                      fontSize: 13,
                      color: '#666',
                      display: 'block',
                      marginBottom: 8,
                      fontWeight: 500,
                      letterSpacing: '0.02em'
                    }}>
                      {item.title}
                    </span>

                    <div style={{
                      fontSize: 36,
                      fontWeight: 800,
                      color: item.color,
                      lineHeight: 1.1,
                      marginBottom: 12,
                      background: item.gradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      {item.value}
                    </div>

                    <span style={{
                      fontSize: 12,
                      color: '#888',
                      display: 'block',
                      marginBottom: 16
                    }}>
                      {item.description}
                    </span>

                    <div style={{
                      height: 3,
                      width: 40,
                      background: item.gradient,
                      borderRadius: 2,
                      margin: '0 auto 16px',
                      opacity: 0.7
                    }} />

                    <div style={{
                      display: 'inline-block',
                      padding: '6px 16px',
                      background: `${item.color}15`,
                      borderRadius: 20,
                      border: `1px solid ${item.color}30`
                    }}>
                      <span style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: item.color,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {idx === 0 ? 'Historique' : idx === 1 ? 'Quotidien' : 'Mensuel'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>
      )}
      
      <div>
        <h3 className="h4 fw-bold mb-2 text-gradient" style={{
          background: 'linear-gradient(135deg, #4BB0C0 0%, #2E8C99 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Les Offres disponibles
        </h3>
        {activeOffers.length > 0 ? (
          <OfferCards
            offers={activeOffers}
            setShowModalOffre={() => { }}
            setSelectedOffer={() => { }}
            handleDelete={async (id: number) => { }}
            userRole={user?.role === "ADMIN" ? "ADMIN" : "CLIENT"}
          />
        ) : (
          <div>Aucune Offre disponible</div>
        )}
      </div>
    </>
  );
};

export default UserQuotaTab;